"use client";
import React, { useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useSearchParams } from 'next/navigation';

import styles from "./EnquiryForm.module.css";

import apiClient from "@/helpers/api.helper";

// Validators
import {
  validateName,
  validateEmail,
  validatePhone,
  validateDOB,
  validateFile,
} from "@/helpers/validator";
import CustomInput from "../ui/CustomInput/CustomInput";
import CustomSelect from "../ui/CustomSelect/CustomSelect";
import Buttons from "../ui/Buttons/Buttons";
import CustomFileUpload from "../ui/CustomFileUpload/CustomFileUpload";
import Stepper from "../ui/Stepper/Stepper";
import { STUDENT_REGISTRATION } from "@/constants/urls";
import type { EnquiryFormState } from "@/models/api/studentRegistration";
import type { StudentRegistrationCreate } from "@/models/api/studentRegistration";
import { COMMON_UTILITIES } from "@/constants/urls";
import type { Utility } from "@/models/api/utility";
import { useQuery } from "@tanstack/react-query";
import { STUDENT_GETCOURSE, DOCUMENT_UPLOAD } from "@/constants/urls";
import { Course } from "@/models/api/course"
import type { CreateEnquiryResponse, UploadResponse } from "@/models/api/studentRegistration";
// import PaymentSection from "../ui/PaymentSection/PaymentSection";
import SubmitSuccessModal from "../ui/SubmitSuccessModal/SubmitSuccessModal";

// ------------------ Dynamic Stepper Config ------------------
const getStepsConfig = (showPayment: boolean) => {
  if (showPayment) {
    return [
      { label: "Student Information" },
      { label: "Payment" },
    ];
  } else {
    return [
      { label: "Student Information" },
    ];
  }

};

// ------------------ Component ------------------
const EnquiryForm: React.FC = () => {
  const router = useRouter();
  const currentYear = new Date().getFullYear();
  // const toIsoFromYMD = (d?: string) => (d ? new Date(`${d}T00:00:00`).toISOString() : "");
const toDateOnly = (d?: string) => (d ? d : "");

  //Url data passing
  const searchParams = useSearchParams();
  const title = searchParams.get('title'); // get course title from URL
  // State
  const [step, setStep] = useState<number>(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPayment, setShowPayment] = useState<boolean>(false); // Set this to true/false based on your requirement
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [enquiryId, setEnquiryId] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [shouldUploadOnSubmit, setShouldUploadOnSubmit] = useState(false);
  const [docNotice, setDocNotice] = useState<string | null>(null);

  const [formData, setFormData] = useState<EnquiryFormState>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    phone: "",
    yearOfAdmission: currentYear.toString(),
    email: "",
    class: "",
    fathersName: "",
    mothersName: "",
    guardianName: "",
    nativeLanguage: "",
    currentCountry: "",
    currentSchool: "",
    currentGrade: "",
    currentSchoolLanguage: "",
    additionalInformation: "",
    referralSource: "",
    englishSupport: "",
    consent: false,
    comments: 'no',
    documents: null,
    hostel: false,
    transportation: false,
    canteen: false,
    paymentMethod: "",
  });

  // ✅ Dynamic steps config
  const stepsConfig = getStepsConfig(showPayment);
  const createOrGetEnquiryId = async (): Promise<string> => {
    if (enquiryId) return enquiryId;

    const payload = buildPayload();
    const { enquiryId: id } =
      await apiClient.post<CreateEnquiryResponse>(STUDENT_REGISTRATION, payload);

    if (!id) throw new Error("No enquiryId returned.");
    setEnquiryId(id);
    return id;
  };

  
  const handleUploadDocument = async (file: File) => {
    setPendingUploadFile(file);
    setShouldUploadOnSubmit(true);
    setDocumentUploaded(false);

    // Clear document error and set a non-blocking notice
    setErrors(prev => ({ ...prev, documents: "" }));
    setDocNotice("Document queued. It will upload after you submit the form.");
  };

  

  const buildPayload = (): StudentRegistrationCreate => {
    const nowIso = new Date().toISOString();

    return {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      dateOfBirth: toDateOnly(formData.dateOfBirth),
      gender: formData.gender,
      currentCountry: formData.currentCountry,
      proposedEntryYear: Number(formData.yearOfAdmission || new Date().getFullYear()),
      currentSchool: formData.currentSchool,
      currentGradeLevel: formData.currentGrade,
      currentSchoolLanguage: formData.currentSchoolLanguage,
      // applyingGradeLevel: formData.class,
      courseId: formData.class,

      nativeLanguage: formData.nativeLanguage,
      eal: formData.englishSupport === "yes",
      email: formData.email,
      phone: formData.phone,
      referralSource: formData.referralSource,
      additionalInfo: formData.additionalInformation,
      consentToDataProcessing: formData.consent,
      fatherName: formData.fathersName,
      motherName: formData.mothersName,
      guardianName: formData.guardianName,
      comments: "No",
      // paymentStatusId: showPayment ? (codeToId[formData.paymentMethod] ?? "") : "no_payment_required",
      createdDate: nowIso,
      modifiedDate: nowIso,
      modifiedBy: "web-form",
      isActive: true,
    };
  };

  const classesQuery = useQuery<Course[], Error>({
    queryKey: ["studentClasses"],
    queryFn: async () => {
      const data = await apiClient.get<Course[]>(STUDENT_GETCOURSE);
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60_000,
  });

  const classOptions = classesQuery.data?.map(c => ({
    value: c.id,
    label: c.displayName,
  })) ?? [];

  const finalCourseOptions = classOptions?.length ? classOptions : [];

  const toValue = (code: string) => code.trim().toLowerCase().replace(/\s+/g, "_");
  const toTitle = (s: string) => s.replace(/[_\-]+/g, " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());

  const paymentUtilsQuery = useQuery<Utility[], Error>({
    queryKey: ["utilities", "PaymentMode"],
    queryFn: async () => {
      const data = await apiClient.get<Utility[]>(COMMON_UTILITIES);
      if (!Array.isArray(data)) return [];
      return data.filter(u => u.type === "PaymentMode" && u.isActive);
    },
    staleTime: 5 * 60_000,
  });

  const paymentOptions = useMemo(() => {
    return (paymentUtilsQuery.data ?? []).map(u => {
      const id = (u as any).Id ?? (u as any).id;
      const code = toValue(u.code);
      const baseLabel = toTitle(u.code);
      const label = u.code === "CREDIT_CARD" ? "Credit Card " : baseLabel;
      return { id, code, label };
    });
  }, [paymentUtilsQuery.data]);

  const codeToId = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of paymentOptions) m[p.code] = p.id;
    return m;
  }, [paymentOptions]);

  

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, type, value } = e.target;
    let newValue: string | boolean | File | null = value;

    if (e.target instanceof HTMLInputElement) {
      if (type === "checkbox") {
        newValue = e.target.checked;
      } else if (type === "file") {
        const file = e.target.files?.[0] ?? null;

        // reset "uploaded" status whenever user selects a (new) file
        setDocumentUploaded(false);

        // clear any previous "queued" notice, since the file just changed
        setDocNotice(null);

        // optional: also clear any blocking error on documents
        setErrors(prev => ({ ...prev, documents: "" }));

        // validate and set error if needed
        if (file && !validateFile(file)) {
          setErrors((prev) => ({ ...prev, [name]: "Invalid file type" }));
        }

        newValue = file; // store the File (or null) in form state
      } else if (type === "number" && name === "yearOfAdmission") {
        const year = parseInt(value, 10);
        newValue = year < currentYear ? currentYear.toString() : value;
      }
    }

    // text validations
    if (["firstName", "lastName", "fathersName", "mothersName"].includes(name)) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateName(value) ? "" : "Only letters allowed, min 2 chars",
      }));
    }
    if (name === "email") {
      setErrors((prev) => ({
        ...prev,
        email: validateEmail(value) ? "" : "Invalid email address",
      }));
    }
    if (name === "phone") {
      setErrors((prev) => ({
        ...prev,
        phone: validatePhone(value) ? "" : "Phone must be 7–15 digits",
      }));
    }
    if (name === "dateOfBirth") {
      setErrors((prev) => ({
        ...prev,
        dateOfBirth: value && !validateDOB(value) ? "Invalid date of birth" : "",
      }));
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  
  const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as EnquiryFormState["paymentMethod"];
    setFormData((prev) => ({ ...prev, paymentMethod: value }));
  };

  const isStepValid = (): boolean => {
    if (step === 1) {
      return (
        formData.firstName.trim() !== "" &&
        formData.dateOfBirth.trim() !== "" &&
        formData.gender.trim() !== "" &&
        formData.class.trim() !== "" &&
        formData.yearOfAdmission.toString().trim() !== "" &&
        validateEmail(formData.email) &&
        validatePhone(formData.phone) &&
        formData.consent &&
        Object.values(errors).every((err) => err === "")
      );
    }

    if (step === 2 && showPayment) {
      return formData.paymentMethod.trim() !== "";
    }

    return true;
  };

  const nextStep = () => {
    console.log("Next step clicked. Current step:", step);
    setStep((prev) => {
      const maxStep = showPayment ? 2 : 1;
      const newStep = Math.min(prev + 1, maxStep)
      console.log("Moving to step:", newStep, "showPayment:", showPayment);
      window.scrollTo(0, 0)
      return newStep
    })
  }

  const prevStep = () => {
    console.log("Previous step clicked. Current step:", step);
    setStep((prev) => {
      const newStep = Math.max(prev - 1, 1)
      console.log("Moving to step:", newStep);
      window.scrollTo(0, 0)
      return newStep
    })
  }

  
  const submitRegistration = async (): Promise<string> => {
    setLoading(true);
    try {
      // Create registration if needed
      let id = enquiryId;
      if (!id) {
        const payload = buildPayload();
        const res = await apiClient.post<CreateEnquiryResponse>(STUDENT_REGISTRATION, payload);
        id = res.enquiryId;
        if (!id) throw new Error("No enquiryId returned.");
        setEnquiryId(id);
      }

      // If user queued a document, upload it now using the new enquiryId
      if (shouldUploadOnSubmit && pendingUploadFile) {
        setUploadingDoc(true);
        setErrors((prev) => ({ ...prev, documents: "" }));

        await apiClient.uploadBlob<UploadResponse>(
          DOCUMENT_UPLOAD,
          pendingUploadFile,
          "File",
          {
            Entity: "Student",
            EntityId: id,
            FileName: pendingUploadFile.name,
          }
        );

        setDocumentUploaded(true);
        setShouldUploadOnSubmit(false);
        setPendingUploadFile(null);
        setUploadingDoc(false);
      }

      setShowSuccessModal(true);
      return id!;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    await submitRegistration();   // returns id & shows modal (as above)
  };

  const renderStep = () => {


    switch (step) {
      case 1:
        return (
          <>
            {/* Admission Enquiry Section */}
            <div className={styles.infoBoxWrapper}>
              <div className={styles.infoBoxHeader}>
                <h1 className={styles.infoTitle}>Admission Enquiry{title ? ` For ${title}` : ''}</h1>
                <p className={styles.infoSubtitle}>Welcome to our community</p>
              </div>

              <p className={styles.infoBoxText}>
                If you&apos;d like to join our college community,
                please complete this form. A college tour is encouraged
                beforehand so you can meet our staff, see our classrooms and
                facilities, and decide if it&apos;s the right fit.
              </p>

              {showPayment ? (
                <div className={styles.nextStepwrapper}>
                  <p className={styles.InfoBoxNext}>
                    <strong>Next Steps:</strong>{" "}
                    {formData.paymentMethod
                      ? " Once submitted and the $250 registration fee is paid, your child will be added to the waiting list. Placement will be confirmed based on sibling priority and waitlist order."
                      : "Once submitted, your enquiry will be reviewed and you will be notified about the next process."}
                  </p>
                </div>
              ) : (
                <div className={styles.nextStepwrapper}>
                  <p className={styles.InfoBoxNext}>
                    <strong>Next Steps:</strong>{" "}
                    Once submitted, your enquiry will be reviewed and you will be notified about the next process.
                  </p>
                </div>
              )}
            </div>

            {/* Student Information */}
            <div className={styles.infoBox}>
              <div className={styles.infoHeader}>
                <h2 className={styles.infoTitle}>Student Information</h2>
                <p className={styles.infoSubtitle}>Tell us about yourself</p>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.inputGroup}>
                  <CustomInput
                    label="First Name"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && <p className={styles.error}>{errors.firstName}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <CustomInput
                    label="Last Name"
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && <p className={styles.error}>{errors.lastName}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <CustomInput
                    label="Date of Birth"
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                  {errors.dateOfBirth && <p className={styles.error}>{errors.dateOfBirth}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <CustomSelect
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    options={[
                      { value: "", label: "Select gender" },
                      { value: "male", label: "Male" },
                      { value: "female", label: "Female" },
                      { value: "other", label: "Other" },
                    ]}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <CustomSelect
                    label="Courses"
                    name="class"
                    value={formData.class}
                    onChange={handleChange}
                    required={!classesQuery.isLoading && !!classOptions?.length}
                    options={finalCourseOptions}
                    disabled={classesQuery.isLoading}
                    placeholder={classesQuery.isLoading ? "Select course" : "Select course"}
                  />
                  {classesQuery.isError && <p className={styles.error}></p>}
                  {errors.class && <p className={styles.error}>{errors.class}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <CustomInput
                    label="Year of Admission"
                    type="number"
                    name="yearOfAdmission"
                    value={formData.yearOfAdmission}
                    onChange={handleChange}
                    placeholder={currentYear.toString()}
                    required
                    min={currentYear}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <CustomInput
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className={styles.error}>{errors.email}</p>}
                </div>

                <div className={styles.inputGroup}>
                  <CustomInput
                    label="Phone Number"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Enter phone number"
                  />
                  {errors.phone && <p className={styles.error}>{errors.phone}</p>}
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className={styles.infoBox}>
              <div className={styles.infoHeader}>
                <h2 className={styles.infoTitle}>Documents</h2>
                <p className={styles.infoSubtitle}>Upload any supporting document</p>
              </div>
              <div className={styles.inputGroup}>
                
                <CustomFileUpload
                  label="Click to upload or drag and drop"
                  name="documents"
                  onChange={handleChange}
                  onUpload={(file) => handleUploadDocument(file)}
                  uploadText={
                    shouldUploadOnSubmit
                      ? "Upload Document"
                      : uploadingDoc
                        ? "Uploading..."
                        : documentUploaded
                          ? "Uploaded"
                          : "Upload Document"
                  }
                  uploadDisabled={shouldUploadOnSubmit || uploadingDoc || loading}
                />
                {errors.documents && <p className={styles.error}>{errors.documents}</p>}
              </div>
            </div>

            {/* Consent */}
            <div className={styles.infoBox}>
              <div className={styles.inputGroup}>
                <label className={styles.checkboxlabel}>
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className={styles.checkboxinput}
                  />
                  <span className={styles.labeltext}>
                    I consent to the processing of my data.
                  </span>
                </label>
              </div>
            </div>

            {/* Buttons - Dynamic based on payment setting */}
            <div className={styles.formButtons}>
              {/* <Buttons
                variant="success"
                onClick={() => router.push("/")}
                className={styles.formButtonPrev}
              >
                Previous
              </Buttons> */}

              {showPayment ? (
                // With Payment - Show Next button
                <Buttons
                  variant="success"
                  onClick={nextStep}
                  disabled={!isStepValid()}
                  className={styles.formButtonNext}
                >
                  Next
                </Buttons>
              ) : (
                // Without Payment - Show Submit button
                <Buttons
                  variant="success"
                  onClick={handleSubmit}
                  disabled={!isStepValid() || loading}
                  className={styles.formButtonNext}
                >
                  {loading ? "Submitting..." : "Submit"}
                </Buttons>
              )}
            </div>
          </>
        );

        // case 2:
        //   // Only show payment section if payment is enabled
        //   if (showPayment) {
        //     return (
        //       <PaymentSection
        //         paymentUtilsQuery={paymentUtilsQuery}
        //         paymentOptions={paymentOptions}
        //         formData={formData}
        //         handlePaymentChange={handlePaymentChange}
        //         handleSubmit={handleSubmit}
        //         prevStep={prevStep}
        //         loading={loading}
        //         showPayment={showPayment}
        //       />
        //     );
        //   }
        return null;

      default:
        return null;
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.steppedHeader}>
        {/* Show stepper only for step 1 when payment is disabled, or for steps 1-2 when payment is enabled */}
        {((showPayment && step <= 2) || (!showPayment && step === 1)) && (
          <Stepper
            steps={stepsConfig}
            currentStep={step}
          />
        )}
      </div>

      {renderStep()}

      {/* Success Modal */}
      {showSuccessModal && (
        <SubmitSuccessModal
          open={true}
          onClose={() => {
            setShowSuccessModal(false);
            router.push("/");
          }}
        />
      )}
    </div>
  );
};

export default EnquiryForm;